/**
 * User Interview Tool
 *
 * Direct tool available to the Main Orchestrator Agent for collecting
 * structured input from users when clarification or additional information is needed.
 *
 * Key behaviors:
 * - Questions are presented one at a time in the UI
 * - All questions are required - users must provide answers to continue
 * - 5 minute timeout - after which the interview is cancelled
 * - If user sends a different chat message, interview is cancelled
 * - Each question has a "Let Carousel decide" option
 */
import { ToolDescription, safeParseToolParams } from "../../agents/base/toolDescription";
import { ToolBase } from "../../agents/base/toolBase";
import { UserInterviewParamsSchema, } from "./userInterview.schema";
let pendingInterview = null;
// 5 minute timeout
const INTERVIEW_TIMEOUT_MS = 5 * 60 * 1000;
// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════
const USER_INTERVIEW_SYSTEM_PROMPT = `### userInterview
Ask the user clarifying questions with interactive selection interfaces.

**When to use:**
- Request is ambiguous or unclear
- Multiple valid interpretations exist
- Need user preference or confirmation
- Missing required information

**Question types:**
- \`text\` - Free-form text input
- \`single_select\` - Pick one option
- \`multi_select\` - Pick multiple options

**Guidelines:**
- Ask 2-3 questions at a time (don't overwhelm)
- Make questions specific and actionable
- Explain why information is needed
- Don't ask what you could infer from context

\`\`\`json
{
  "questions": [
    {
      "id": "q1",
      "type": "single_select",
      "prompt": "Which region should we focus on?",
      "options": ["Americas", "EMEA", "APAC", "Global"]
    }
  ]
}
\`\`\``;
// ═══════════════════════════════════════════════════════════════════════════
// TOOL IMPLEMENTATION
// ═══════════════════════════════════════════════════════════════════════════
export class UserInterviewTool extends ToolBase {
    constructor() {
        super();
        this.name = "userInterview";
        // System prompt description
        this.systemPromptDescription = USER_INTERVIEW_SYSTEM_PROMPT;
        // Streaming operation fields (merged from UserInterviewOperation)
        this.fieldsToCheck = [
            { name: "questions", type: "array", list_status: true, order: 1 },
        ];
        this.listsToCheck = [
            {
                name: "questions",
                type: "array",
                item_type: "object",
                list_status: true,
                order: 1,
                check_sub_field: "prompt", // Question is ready when prompt is present
            },
        ];
        // Accumulated state for questions
        this._questions = [];
        this.description = new ToolDescription("userInterview", `Ask the user clarifying questions with interactive selection interfaces.

Use this tool when you need user input to proceed with a task. Questions are presented one at a time.

Question types:
- multiple_choice: User selects ONE option (for single selections, confirmations)
- multiple_selection: User selects MULTIPLE options (for batch operations, multi-file selection)

Each question includes:
- Pre-populated options
- Option to add custom response
- "Let Carousel decide" option (user defers to AI)

All questions are required - users must provide answers to continue.

Common use cases:
- File selection: "Which Excel file should I edit?"
- Confirmation: "I found 47 slides with the old logo. Replace all?"
- Multi-selection: "Which sheets should I update?"

Example:
{
  "questions": [{
    "id": "excel_file",
    "type": "multiple_choice",
    "prompt": "Which Excel file contains the chart data?",
    "options": ["Recent: Q4-Model.xlsx", "Linked: Revenue.xlsx", "Browse for file..."]
  }]
}`, UserInterviewParamsSchema);
    }
    /**
     * Override addStep to track questions
     */
    addStep(fieldName, value) {
        super.addStep(fieldName, value);
        if (fieldName === "questions" && Array.isArray(value)) {
            this._questions = value;
        }
        else if (fieldName === "questions_item" && value) {
            // Individual question item streamed in
            this._questions.push(value);
        }
    }
    /**
     * Override reset to clear questions
     */
    reset() {
        super.reset();
        this._questions = [];
    }
    /**
     * Get all accumulated questions
     */
    getQuestions() {
        return this._questions;
    }
    /**
     * Execute the tool - returns a promise that resolves when user responds
     */
    async execute(parameters) {
        const parseResult = safeParseToolParams(UserInterviewParamsSchema, parameters);
        if (parseResult.success === false) {
            console.error('[UserInterviewTool] Invalid parameters:', parseResult.error);
            return {
                answers: [],
                status: "cancelled",
                cancelReason: `Invalid parameters: ${parseResult.error}`,
            };
        }
        const params = parseResult.data;
        const interviewId = `interview_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        console.log(`[UserInterviewTool] Starting interview ${interviewId} with ${params.questions.length} questions`);
        // Create promise that will be resolved when user responds
        return new Promise((resolve, reject) => {
            // Set timeout
            const timeoutId = setTimeout(() => {
                if (pendingInterview && pendingInterview.id === interviewId) {
                    console.log(`[UserInterviewTool] Interview ${interviewId} timed out`);
                    pendingInterview = null;
                    resolve({
                        answers: [],
                        status: "timeout",
                        cancelReason: "You took too long to respond. The question has been cancelled.",
                    });
                }
            }, INTERVIEW_TIMEOUT_MS);
            // Store pending state
            pendingInterview = {
                id: interviewId,
                questions: params.questions,
                resolve,
                reject,
                timeoutId,
                createdAt: Date.now(),
            };
            // The UI will detect the pending interview via getPendingInterview()
            // and render the UserInterviewBlock component
            console.log(`[UserInterviewTool] Interview ${interviewId} waiting for user response...`);
        });
    }
    // ═══════════════════════════════════════════════════════════════════════
    // STATIC METHODS (called by UI/external code)
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Called by UI when user submits answers
     */
    static submitAnswers(answers) {
        if (pendingInterview) {
            console.log(`[UserInterviewTool] Interview ${pendingInterview.id} completed with ${answers.length} answers`);
            clearTimeout(pendingInterview.timeoutId);
            const resolve = pendingInterview.resolve;
            pendingInterview = null;
            resolve({
                answers,
                status: "completed",
            });
        }
        else {
            console.warn('[UserInterviewTool] submitAnswers called but no pending interview');
        }
    }
    /**
     * Called when user sends a different chat message (cancels interview)
     */
    static cancelInterview(reason) {
        if (pendingInterview) {
            console.log(`[UserInterviewTool] Interview ${pendingInterview.id} cancelled: ${reason || 'user action'}`);
            clearTimeout(pendingInterview.timeoutId);
            const resolve = pendingInterview.resolve;
            pendingInterview = null;
            resolve({
                answers: [],
                status: "skipped",
                cancelReason: reason || "User didn't respond to your questions. Instead, they elected to continue chatting. Please continue the conversation with them.",
            });
        }
    }
    /**
     * Check if there's a pending interview
     */
    static hasPendingInterview() {
        return pendingInterview !== null;
    }
    /**
     * Get pending interview data for UI rendering
     */
    static getPendingInterview() {
        if (!pendingInterview)
            return null;
        return {
            id: pendingInterview.id,
            questions: pendingInterview.questions,
        };
    }
    /**
     * Get the interview ID (for component keying)
     */
    static getPendingInterviewId() {
        return pendingInterview?.id ?? null;
    }
}
export default UserInterviewTool;
